import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import Chart from "react-google-charts";
import { usePDF } from "react-to-pdf";

function CustomReportPage() {
  // Estados para totales (widgets tipo 'suma')
  const [sessionTotal, setSessionTotal] = useState(0);
  const [billingTotal, setBillingTotal] = useState("");
  const [customerTotal, setCustomerTotal] = useState(0);

  // Estados para gráficos (widgets tipo 'grafico')
  const [historicalSessions, setHistoricalSessions] = useState<any[][]>([
    ["Día", "Sesiones"],
  ]);
  const [historicalBilling, setHistoricalBilling] = useState<any[][]>([
    ["Día", "Facturación"],
  ]);
  const [historicalCustomers, setHistoricalCustomers] = useState<any[][]>([
    ["Día", "Clientes"],
  ]);

  useEffect(() => {
    const startDate = new Date("2025-07-01");
    const endDate = new Date("2025-07-31");
    const sessions: any[][] = [["Día", "Sesiones"]];
    const billings: any[][] = [["Día", "Facturación"]];
    const customers: any[][] = [["Día", "Clientes"]];

    let totalSessions = 0;
    let totalBilling = 0;
    let totalCustomers = 0;
    const curr = new Date(startDate);

    while (curr <= endDate) {
      const rndSessions = Math.floor(Math.random() * 6) + 5; // 5-10 sesiones por día
      const rndBilling = rndSessions * (Math.floor(Math.random() * 11) + 30); // 30-40€ por sesión
      const rndCustomers = Math.floor(Math.random() * 2); // 0-1 clientes nuevos por día

      const dayStr = curr.toLocaleDateString("es-ES");
      sessions.push([dayStr, rndSessions]);
      billings.push([dayStr, rndBilling]);
      customers.push([dayStr, rndCustomers]);

      totalSessions += rndSessions;
      totalBilling += rndBilling;
      totalCustomers += rndCustomers;
      curr.setDate(curr.getDate() + 1);
    }

    setSessionTotal(totalSessions);

    // Formatear facturación omitiendo decimales si son ,00
    const formattedBilling = new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(totalBilling);
    setBillingTotal(formattedBilling);

    setCustomerTotal(totalCustomers);
    setHistoricalSessions(sessions);
    setHistoricalBilling(billings);
    setHistoricalCustomers(customers);
  }, []);

  // Hook para exportar a PDF
  const { toPDF, targetRef } = usePDF({ filename: "reporte-julio-2025.pdf" });

  // Función para exportar a CSV
  const exportToCSV = () => {
    let csvContent = "";

    // -------- Sección Resumen --------
    csvContent += "Resumen Mes Julio 2025\n";
    csvContent += "Métrica,Valor\n";
    csvContent += `Total Sesiones,${sessionTotal}\n`;
    csvContent += `Total Facturación,${billingTotal}\n`;
    csvContent += `Total Clientes,${customerTotal}\n\n`;

    // -------- Sección Histórico Sesiones --------
    csvContent += "Sesiones por día\n";
    csvContent += "Día,Sesiones\n";
    historicalSessions.slice(1).forEach(([día, ses]) => {
      csvContent += `${día},${ses}\n`;
    });

    csvContent += "\n";

    // -------- Sección Histórico Facturación --------
    csvContent += "Facturación por día\n";
    csvContent += "Día,Facturación\n";
    historicalBilling.slice(1).forEach(([día, fac]) => {
      csvContent += `${día},${fac}\n`;
    });

    csvContent += "\n";

    // -------- Sección Histórico Clientes --------
    csvContent += "Clientes por día\n";
    csvContent += "Día,Clientes\n";
    historicalCustomers.slice(1).forEach(([día, cli]) => {
      csvContent += `${día},${cli}\n`;
    });

    // -------- Descargar como archivo .csv --------
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "reporte-julio-2025.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container" ref={targetRef}>
      <div className="d-flex justify-content-end mb-3">
        <button className="btn btn-success" onClick={exportToCSV}>
          Exportar CSV
        </button>
        <button className="btn btn-primary ms-2" onClick={() => toPDF()}>
          Exportar PDF
        </button>
      </div>

      {/* Encabezado del reporte */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Mes Julio</h3>
        <span>2025-07-01 - 2025-07-31</span>
      </div>

      {/* Widgets tipo 'suma' - Totales */}
      <div className="row">
        <div className="col-4">
          <Card>
            <Card.Header>Total de sesiones</Card.Header>
            <Card.Body>
              <div className="report">{sessionTotal}</div>
            </Card.Body>
          </Card>
        </div>
        <div className="col-4">
          <Card>
            <Card.Header>Total de facturación</Card.Header>
            <Card.Body>
              <div className="report">{billingTotal}</div>
            </Card.Body>
          </Card>
        </div>
        <div className="col-4">
          <Card>
            <Card.Header>Total de clientes</Card.Header>
            <Card.Body>
              <div className="report">{customerTotal}</div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Widgets tipo 'grafico' */}
      <div className="row mt-3">
        <div className="col-12">
          <Card>
            <Card.Header>Sesiones por día</Card.Header>
            <Card.Body>
              <Chart
                chartType="Bar"
                data={historicalSessions}
                width="100%"
                height="400px"
                legendToggle
              />
            </Card.Body>
          </Card>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <Card>
            <Card.Header>Facturación por día</Card.Header>
            <Card.Body>
              <Chart
                chartType="Bar"
                data={historicalBilling}
                width="100%"
                height="400px"
                legendToggle
              />
            </Card.Body>
          </Card>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-12">
          <Card>
            <Card.Header>Clientes por día</Card.Header>
            <Card.Body>
              <Chart
                chartType="Bar"
                data={historicalCustomers}
                width="100%"
                height="400px"
                legendToggle
              />
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default CustomReportPage;
